import { FC, memo, useEffect } from "react"
import { useSnapshot } from "valtio"
import { alpha, Box, GlobalStyles, styled } from "@mui/material"
import ContentRenderer from "../../../components/common/ContentRenderer"
import { ScreenReaderAnnouncer } from "../../../components/common/ScreenReaderAnnouncer"
import { QuestionFeedback, QuestionGroupFeedback } from "../../../feedback"
import { InfoControlLayout } from "../../../ui/layouts/InfoControlLayout"
import { InteractionLayout } from "../../../ui/layouts/InteractionLayout"
import { useAssessmentItem, useAssessmentItemStyles } from "../../../helpers"
import {
  CurrentTestState,
  CurrentUserAssessmentState,
  ScoringState
} from "../../../storage"
import {
  AssessmentStatus,
  CheckCorrect,
  isQuestionComplete,
  onSaveStudentAnswer,
  NeutralSetting
} from "../../../types"

interface AssessmentItemProps {
  isTest?: boolean
  showAnswer?: boolean
  onSaveStudentAnswer?: onSaveStudentAnswer
  checkCorrect?: CheckCorrect
  isCompleted?: isQuestionComplete
  containerType?: "screen" | "exercise" | "assessment"
  isNeutralSetting?: NeutralSetting
  // Used for fetching Tables in QTI Interaction
  QtiTableContent?: React.FC<{ tableId: string; identifier?: string }>
  // a11y: resolves the live Media Library alt for QTI images (Ready#7894/#7937); the app wires
  // this to its live asset lookup so an empty inline alt falls back to the asset altText.
  resolveAlt?: (assetIdOrSrc: string) => string | undefined
}

const AssessmentItem: FC<AssessmentItemProps> = props => {
  const {
    isTest = true,
    showAnswer = false,
    onSaveStudentAnswer,
    checkCorrect,
    isCompleted: isCompletedQuestion,
    containerType,
    isNeutralSetting,
    QtiTableContent,
    resolveAlt
  } = props
  const { currentItemResponse, currentTestPart } = useSnapshot(CurrentTestState)
  const { status } = useSnapshot(CurrentUserAssessmentState)
  const { groupMode, contentPreviewMode } = useSnapshot(ScoringState)

  const { currentItem, interaction, infoControl, hasInlineInteractions } =
    useAssessmentItem()

  const styles = useAssessmentItemStyles(currentItem)

  const isCompleted = status === AssessmentStatus.completed

  useEffect(() => {
    console.log(currentTestPart)
  }, [currentTestPart])

  return (
    <AssessmentItemWrapper
      isTest={isTest}
      component="main"
      aria-label="Question area"
    >
      {styles && <GlobalStyles styles={styles} />}
      <ScreenReaderAnnouncer />

      {currentItem ? (
        <>
          {infoControl.trim() && (
            <InfoControlLayout>
              <ContentRenderer
                itemId={currentItem?.id}
                content={infoControl}
                resourceId={currentItem?.resourceId}
                containerType={containerType}
                QtiTableContent={QtiTableContent} // Used for rendering Tables in QTI Interaction
                resolveAlt={resolveAlt}
              />
            </InfoControlLayout>
          )}
          {currentItemResponse?.id && interaction && (
            <InteractionLayout
              itemId={currentItemResponse?.itemId}
              large={currentTestPart?.twoColumnLayout}
              reviewMode={isCompleted}
              withHeader={isTest}
              withFooter={isTest}
            >
              <ContentRenderer
                itemId={currentItem?.id}
                content={interaction}
                resourceId={currentItem?.resourceId}
                showAnswer={showAnswer}
                onSaveStudentAnswer={onSaveStudentAnswer}
                checkCorrect={checkCorrect}
                isCompletedQuestion={isCompletedQuestion}
                containerType={containerType}
                isNeutralSetting={isNeutralSetting}
                QtiTableContent={QtiTableContent} // Used for rendering Tables in QTI Interaction
                resolveAlt={resolveAlt}
              />
              {hasInlineInteractions &&
                isTest &&
                !contentPreviewMode &&
                (groupMode ? (
                  <QuestionGroupFeedback itemId={currentItem?.id} />
                ) : (
                  <QuestionFeedback itemId={currentItem?.id} />
                ))}
            </InteractionLayout>
          )}
        </>
      ) : (
        <span>Condition not passed</span>
      )}
    </AssessmentItemWrapper>
  )
}

interface AssessmentItemWrapperProps {
  isTest?: boolean
}

export const AssessmentItemWrapper = styled(Box, {
  shouldForwardProp: prop => prop !== "isTest"
})<AssessmentItemWrapperProps>(
  ({ theme, isTest }) => `
  position: relative;
  padding: ${theme.spacing(4, 8)};
  display: flex;
  justify-content: center;
  gap: ${theme.spacing(6)};
  overflow: hidden;
  height: 100%;
  ${isTest ? `background-color: ${alpha(theme.palette.grey[50], 0.4)};` : ""}

  &:focus {
    outline: none;
  }

  ${theme.breakpoints.down("mobile")} {
    padding: ${theme.spacing(3)};
    flex-direction: column;
    gap: ${theme.spacing(3)};
  }
`
)

export const AssessmentItemComponent = memo(AssessmentItem)
